import { useCallback, useRef } from 'react'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { InfiniteData } from '@tanstack/react-query'
import { ChangeSet, NotificationChange } from '@/types/doc-changes'
import PaginatedResponse from '@/types/paginated-response'
import { DocumentsChangeSetsContextValue } from '@/components/validation/providers/DocumentsChangeSetsProvider'
import axios from '../api-axios'
import useDebouncePromise from '../core-hooks/useDebouncePromise'
import useMutation, { UseMutationOptions } from '../core-hooks/useMutation'
import { UseUpdateListOptions } from '../core-hooks/useUpdateList'
import { Filters } from '../request-types'
import { filtersToQueryString } from '../request-wrappers'

export type DocChangesRunResult = {
  /** False when the server answered 202: the run was queued instead of
   * finishing, and its result will arrive over the websocket. */
  completed: boolean
  changes: NotificationChange[]
}

type OnMutateResult = {
  previous?: InfiniteData<PaginatedResponse<ChangeSet>>
  batchId?: string
} | void

// Borrows the shared list-update options for everything except the callbacks
// that carry the resolved value — this hook answers with a run result, not
// with a list of change sets.
export type UseCreateDocumentChangeSetsOptions = {
  documentsChangeSetsContextData?: DocumentsChangeSetsContextValue
} & Omit<
  UseUpdateListOptions<ChangeSet>,
  'mutationFn' | 'onSuccess' | 'onSettled'
> &
  Pick<
    UseMutationOptions<
      DocChangesRunResult,
      unknown,
      ChangeSet[],
      OnMutateResult
    >,
    'onSuccess' | 'onSettled'
  >

function postChangeSets(
  items: ChangeSet[],
  filters?: Filters,
  axiosOptions?: AxiosRequestConfig,
) {
  const searchParams = filtersToQueryString(filters)
  return axios.patch<ChangeSet[], AxiosResponse<NotificationChange[] | ''>>(
    `/v2/pd-rules/doc_changes/changes?${searchParams}`,
    items,
    axiosOptions,
  )
}

// A notification never says which doc it belongs to, only which change sets
// it answers.
function findDocumentId(
  documentsChangeSetIds: Record<string, Set<string>>,
  changeSetIds: string[],
) {
  return Object.entries(documentsChangeSetIds).find(([, ids]) =>
    changeSetIds.some((id) => ids.has(id)),
  )?.[0]
}

/**
 * MARK: Action Hook: useCreateDocumentChangeSets
 * Creates document change sets on the synchronous endpoint, which answers
 * either with the changes it made, or with a 202 when the run took too long
 * and the result will arrive over the websocket instead.
 */
export function useCreateDocumentChangeSets({
  documentsChangeSetsContextData,
  onMutate,
  onError,
  filters,
  axiosOptions,
  ...options
}: UseCreateDocumentChangeSetsOptions = {}) {
  const {
    documentsChangeSetIdsRef,
    setDocumentLastChangeSetSentAt,
    changeAppliersRef,
    markChangeSetIdsApplied,
    tryConsumeAppliedChangeSetIds,
    clearPendingChangeSetIds,
  } = documentsChangeSetsContextData || {}

  const batchRef = useRef<Record<string, ChangeSet>>({})

  // Everyone whose mutate() call is waiting on the next debounced request.
  const waitingCallersRef = useRef<
    {
      resolve: (value: DocChangesRunResult) => void
      reject: (reason?: unknown) => void
    }[]
  >([])

  const handleResponse = useCallback(
    (
      batchedItems: ChangeSet[],
      response: AxiosResponse<NotificationChange[] | ''>,
    ) => {
      // A 202 means the run is still queued, so leave every change set
      // pending and let the websocket wait handle it.
      if (response.status === 202) return { completed: false, changes: [] }

      // A finished run always answers with a list — empty when the rules
      // engine changed nothing. Any other shape means we never reached the
      // rules engine, so fail and let the caller refetch.
      if (!Array.isArray(response.data)) {
        throw new Error(
          `Expected a list of document changes from the rules engine, ` +
            `got ${typeof response.data} with status ${response.status}.`,
        )
      }

      const notifications = response.data

      notifications.forEach((notification) => {
        // The websocket copy can beat the response here, so skip whatever it
        // already applied.
        const alreadyApplied = tryConsumeAppliedChangeSetIds?.(
          notification.change_set_ids,
        )
        if (alreadyApplied) return

        markChangeSetIdsApplied?.(notification.change_set_ids)

        const docId = documentsChangeSetIdsRef
          ? findDocumentId(
              documentsChangeSetIdsRef.current,
              notification.change_set_ids,
            )
          : undefined

        if (docId) changeAppliersRef?.current[docId]?.(notification)
      })

      // A change set can finish with no entry in the body at all, like a
      // noop. The run still completed, so stop waiting on it either way.
      batchedItems.forEach((changeSet) => {
        clearPendingChangeSetIds?.(changeSet.doc_id, [changeSet.id])
      })

      return { completed: true, changes: notifications }
    },
    [
      changeAppliersRef,
      clearPendingChangeSetIds,
      tryConsumeAppliedChangeSetIds,
      documentsChangeSetIdsRef,
      markChangeSetIdsApplied,
    ],
  )

  const debouncedRequest = useDebouncePromise({
    fn: () => {
      const batchedItems = Object.values(batchRef.current)
      batchRef.current = {}

      // Debouncing runs the request once for everyone who called during the
      // window, so take the whole waiting list and settle all of them.
      const waiting = waitingCallersRef.current
      waitingCallersRef.current = []

      postChangeSets(batchedItems, filters, axiosOptions)
        .then((response) => {
          const applied = handleResponse(batchedItems, response)
          waiting.forEach(({ resolve }) => resolve(applied))
        })
        .catch((error) => {
          // One request carries change sets from every caller batched into
          // it, so unwind them all here — a caller's own error handler only
          // knows about the change sets it sent.
          batchedItems.forEach((changeSet) => {
            clearPendingChangeSetIds?.(changeSet.doc_id, [changeSet.id])
          })
          waiting.forEach(({ reject }) => reject(error))
        })
    },
  })

  const mutation = useMutation<
    DocChangesRunResult,
    unknown,
    ChangeSet[],
    OnMutateResult
  >({
    mutationFn: (items) => {
      items.forEach((item) => {
        batchRef.current[item.id] = item
      })
      return new Promise<DocChangesRunResult>((resolve, reject) => {
        waitingCallersRef.current.push({ resolve, reject })
        debouncedRequest()
      })
    },
    onMutate: (changeSets, context) => {
      if (documentsChangeSetIdsRef) {
        const documentsChangeSetIds = documentsChangeSetIdsRef.current
        changeSets.forEach((changeSet) => {
          // A noop change set is a fire-and-forget cache refresh — the server
          // never sends back a result for it, so nothing should wait on it.
          if (changeSet.options?.noop) return

          documentsChangeSetIds[changeSet.doc_id] ||= new Set()
          documentsChangeSetIds[changeSet.doc_id].add(changeSet.id)
          setDocumentLastChangeSetSentAt?.((prev) => ({
            ...prev,
            [changeSet.doc_id]: new Date(),
          }))
        })
      }

      return onMutate?.(changeSets, context)
    },
    onError,
    ...options,
  })

  return {
    createDocumentChangeSets: mutation.mutate,
    createDocumentChangeSetsAsync: mutation.mutateAsync,
    ...mutation,
  }
}
